import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { MarkdownRenderer } from '@/components/ui/markdown-renderer'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { useState, useEffect } from 'react'
import {
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  Tag,
  Link as LinkIcon,
  FileText,
  AlertCircle,
  ExternalLink,
  Database,
  BookOpen,
  Mail,
  ChevronLeft,
  ChevronRight,
  Shuffle
} from 'lucide-react'

interface ImportSource {
  uid: string
  source_type: string
  source_name: string
  source_url: string | null
  raw_content: string | null
  status: string
  created_at: string
}

export interface FAQDetailProps {
  faq: any
  onEdit?: () => void
  onClose?: () => void
  onNavigateNext?: () => void
  onNavigatePrevious?: () => void
  onNavigateRandom?: () => void
  hasNext?: boolean
  hasPrevious?: boolean
}

export function FAQDetail({
  faq,
  onEdit,
  onClose,
  onNavigateNext,
  onNavigatePrevious,
  onNavigateRandom,
  hasNext = false,
  hasPrevious = false
}: FAQDetailProps) {
  const [importSource, setImportSource] = useState<ImportSource | null>(null)
  const [showSourceModal, setShowSourceModal] = useState(false)
  const [relatedSources, setRelatedSources] = useState<ImportSource[]>([])

  if (!faq) return null

  // Keyboard shortcuts for navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input or modal is open
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || showSourceModal) {
        return
      }

      switch (e.key.toLowerCase()) {
        case 'arrowright':
        case 'n':
          e.preventDefault()
          if (hasNext && onNavigateNext) onNavigateNext()
          break
        case 'arrowleft':
        case 'p':
          e.preventDefault()
          if (hasPrevious && onNavigatePrevious) onNavigatePrevious()
          break
        case 'r':
          e.preventDefault()
          if (onNavigateRandom) onNavigateRandom()
          break
        case 'e':
          e.preventDefault()
          if (onEdit) onEdit()
          break
        case 'escape':
          e.preventDefault()
          if (onClose) onClose()
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [hasNext, hasPrevious, onNavigateNext, onNavigatePrevious, onNavigateRandom, onEdit, onClose, showSourceModal])

  useEffect(() => {
    if (faq.source_uid) {
      fetchImportSource()
    }
  }, [faq.source_uid])

  const fetchImportSource = async () => {
    try {
      const { data, error } = await supabase
        .from('import_sources')
        .select('*')
        .eq('uid', faq.source_uid)
        .single()

      if (error) throw error
      setImportSource(data)

      // Fetch related sources from same cluster/type
      if (data) {
        const { data: related } = await supabase
          .from('import_sources')
          .select('*')
          .eq('source_type', data.source_type)
          .neq('uid', data.uid)
          .limit(3)

        setRelatedSources(related || [])
      }
    } catch (error) {
      console.error('Error fetching import source:', error)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'draft':
        return <Clock className="h-5 w-5 text-yellow-500" />
      case 'review':
        return <AlertCircle className="h-5 w-5 text-blue-500" />
      case 'archived':
        return <XCircle className="h-5 w-5 text-gray-500" />
      default:
        return null
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'neoom': return BookOpen
      case 'notion': return FileText
      case 'email': return Mail
      default: return Database
    }
  }

  const getSourceName = (sourceType: string) => {
    switch (sourceType) {
      case 'neoom': return 'Neoom Wiki'
      case 'notion': return 'Notion Wiki'
      case 'email': return 'Email'
      default: return sourceType
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon(faq.status)}
            <div>
              <CardTitle className="text-2xl">{faq.question}</CardTitle>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                {faq.category && (
                  <Badge variant="outline">{faq.category.replace('_', ' ')}</Badge>
                )}
                {faq.problem_type && (
                  <Badge variant="secondary">{faq.problem_type}</Badge>
                )}
                <Badge variant={faq.status === 'approved' || faq.status === 'active' ? 'default' : 'secondary'}>
                  {faq.status}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Article Navigation */}
            <div className="flex items-center border rounded-lg p-1 bg-muted/30">
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigatePrevious}
                disabled={!hasPrevious}
                className="h-8 w-8 p-0"
                title="Vorheriger Artikel (← oder P)"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigateRandom}
                className="h-8 w-8 p-0 mx-1"
                title="Zufälliger Artikel (R)"
              >
                <Shuffle className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigateNext}
                disabled={!hasNext}
                className="h-8 w-8 p-0"
                title="Nächster Artikel (→ oder N)"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            {onEdit && (
              <Button onClick={onEdit} size="sm" title="Bearbeiten (E)">
                <Edit className="mr-2 h-4 w-4" />
                Bearbeiten
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Main Answer */}
        <div>
          <h3 className="font-semibold text-lg mb-2">Antwort</h3>
          <MarkdownRenderer content={faq.answer} />
        </div>

        <Separator />

        {/* Question Variations */}
        {faq.question_variations && faq.question_variations.length > 0 && (
          <>
            <div>
              <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Fragevariationen
              </h3>
              <ul className="space-y-2">
                {faq.question_variations.map((variation: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <span className="text-muted-foreground mr-2">•</span>
                    <span className="text-sm">{variation}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Separator />
          </>
        )}

        {/* Solution Steps */}
        {faq.solution_content?.steps && faq.solution_content.steps.length > 0 && (
          <>
            <div>
              <h3 className="font-semibold text-lg mb-3">Lösungsschritte</h3>
              <ol className="space-y-3">
                {faq.solution_content.steps.map((step: string, index: number) => (
                  <li key={index} className="flex">
                    <span className="font-medium mr-3 text-primary">{index + 1}.</span>
                    <span className="flex-1">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
            <Separator />
          </>
        )}

        {/* Helpful Links */}
        {faq.solution_content?.links && faq.solution_content.links.length > 0 && (
          <>
            <div>
              <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Hilfreiche Links
              </h3>
              <ul className="space-y-2">
                {faq.solution_content.links.map((link: string, index: number) => (
                  <li key={index}>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm flex items-center gap-1"
                    >
                      <LinkIcon className="h-3 w-3" />
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <Separator />
          </>
        )}

        {/* Tags */}
        {faq.tags && faq.tags.length > 0 && (
          <>
            <div>
              <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {faq.tags.map((tag: string) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* Metadata */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-sm mb-3">Metadaten</h3>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Erstellt am</p>
                <p className="font-medium">{formatDate(faq.created_at)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground">Aktualisiert am</p>
                <p className="font-medium">{formatDate(faq.updated_at)}</p>
              </div>
            </div>

            {faq.approved_by && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Genehmigt von</p>
                  <p className="font-medium">{faq.approved_by}</p>
                </div>
              </div>
            )}

            {faq.approved_at && (
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Genehmigt am</p>
                  <p className="font-medium">{formatDate(faq.approved_at)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-2 gap-4 text-sm pt-2">
            {faq.quality_score !== null && faq.quality_score !== undefined && (
              <div>
                <p className="text-muted-foreground mb-2">Qualitätsbewertung</p>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full transition-all duration-500 rounded-full",
                        faq.quality_score < 0.3 ? "bg-red-500" :
                        faq.quality_score < 0.7 ? "bg-yellow-500" : "bg-green-500"
                      )}
                      style={{ width: `${Math.min(faq.quality_score * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {faq.usage_count !== null && faq.usage_count !== undefined && (
              <div>
                <p className="text-muted-foreground">Verwendungen</p>
                <p className="font-medium">{faq.usage_count}</p>
              </div>
            )}

            {faq.human_verified && (
              <div>
                <p className="text-muted-foreground">Verifiziert</p>
                <p className="font-medium text-green-600">✓ Ja</p>
              </div>
            )}

            {faq.escalation_rule && (
              <div>
                <p className="text-muted-foreground">Eskalation</p>
                <p className="font-medium">{faq.escalation_rule.replace('_', ' ')}</p>
              </div>
            )}
          </div>

          {importSource && (
            <div className="text-sm pt-2">
              <p className="text-muted-foreground mb-2">Primärquelle</p>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSourceModal(true)}
                  className="h-auto p-2 hover:bg-muted/30 hover:text-foreground flex items-center gap-2"
                >
                  {(() => {
                    const SourceIcon = getSourceIcon(importSource.source_type)
                    return <SourceIcon className="h-4 w-4" />
                  })()}
                  <div className="text-left">
                    <p className="text-sm font-medium">{getSourceName(importSource.source_type)}</p>
                    <p className="text-xs text-muted-foreground">{importSource.uid}</p>
                  </div>
                  <ExternalLink className="h-3 w-3 opacity-50" />
                </Button>

                {relatedSources.length > 0 && (
                  <div className="flex items-center gap-1 ml-2">
                    <span className="text-xs text-muted-foreground">+</span>
                    {relatedSources.slice(0, 3).map((source) => {
                      const RelatedIcon = getSourceIcon(source.source_type)
                      return (
                        <Button
                          key={source.uid}
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setImportSource(source)
                            setShowSourceModal(true)
                          }}
                          className="h-6 w-6 opacity-60 hover:opacity-100"
                        >
                          <RelatedIcon className="h-3 w-3" />
                        </Button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>

      {/* Source Modal */}
      <Dialog open={showSourceModal} onOpenChange={setShowSourceModal}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="text-lg font-semibold">
              {importSource && `${getSourceName(importSource.source_type)}: ${importSource.uid}`}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Primärquelle dieses Knowledge Records
            </p>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6">
            <ScrollArea className="h-[calc(95vh-120px)] w-full">
              <div className="bg-muted rounded-lg p-4">
                <pre className="text-sm font-mono whitespace-pre-wrap break-words">
                  {importSource?.raw_content || 'Kein Inhalt verfügbar'}
                </pre>
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  )
}